import { subject } from '@casl/ability';
import React from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useAbility } from '@/shared/features/ability/context/ability-context';
import { usePermissions } from '@/shared/features/ability/hooks/use-permissions/use-permissions';
import { TAction } from '@/shared/features/ability/hooks/use-user-ability/use-user-ability.types';
import { AddTagMenuItem } from '@/shared/features/tags/components/tag-select/add-tag-menu-item';
import { TTagSelectProps } from '@/shared/features/tags/components/tag-select/tag-select.types';
import { useReadAllTagsQuery } from '@/shared/redux/api/assets/tags/tags-api';
import { ReadAllTagsQuery, TagFragment } from '@/shared/types/types.generated';

export const TagSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  tagFilter,
  getItemId = (value) => value as string,
  createOption,
  disableTags = false,
  additionalOptions = [],
  onSelectChange,
  disableTagCreation = false,
  label,
  hint,
  entity,
  ...props
}: TTagSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<TFieldValues>();
  const alreadySelectedTags = useWatch({ name, control });
  const ability = useAbility();
  const hasPermissionToCreate = usePermissions({
    action: 'create',
    entity: 'Tag',
  });
  const shouldRenderTagsInput = usePermissions({
    action: (alreadySelectedTags // if the tags are not undefined we assume it is an update operation
      ? [`link${entity}`, `unlink${entity}`] // if it is update operation we check both link and unlink permissions
      : [`link${entity}`]) as TAction[], // if it is create operation we check only link permission
    entity: 'Tag',
    relation: alreadySelectedTags ? 'or' : 'and', // if there are no tags and user has no permission to link, we don't render the input, but if there are tags and user has no permission to unlink, we still render the input
  });

  const createDefaultOption = (tag: TagFragment) => {
    const attribute = { id: tag.name };
    const unlinkAction = `unlink${entity}` as TAction;
    const canUnlinkTag = ability.can(
      unlinkAction,
      subject('Tag', { attribute }),
    );

    const disableSelectedTag =
      !canUnlinkTag && alreadySelectedTags?.includes(tag.name);
    const linkAction = `link${entity}` as TAction;
    const canLinkTag = ability.can(linkAction, subject('Tag', { attribute }));
    const disableUnselectedTag =
      !canLinkTag && !alreadySelectedTags?.includes(tag.name);

    return {
      label: tag.name,
      value: tag.name,
      disabled: disableSelectedTag || disableUnselectedTag,
    } as TSelectOption<TOptionShape>;
  };

  const _createOption = createOption ? createOption : createDefaultOption;

  const transformData = (data?: ReadAllTagsQuery) => {
    if (!data) {
      return [];
    }
    const tags = data.readAllTags.items;
    const validTags = tagFilter ? tags.filter((tag) => tagFilter(tag)) : tags;

    const options = validTags.map((tag) => _createOption(tag));

    return [...additionalOptions, ...options];
  };

  const _label = label ?? t('tags:tag_select.label');
  const placeholder =
    disableTagCreation || !hasPermissionToCreate
      ? undefined
      : t('tags:tag_select.placeholder');

  const getRenderNoOptions = (
    inputValue: string,
    resetInputValue: () => void,
  ) => {
    if (disableTagCreation || !hasPermissionToCreate) {
      return undefined;
    }

    return (
      <AddTagMenuItem
        name={inputValue}
        onSuccess={(createdTag) => {
          resetInputValue();
          const currentTags = alreadySelectedTags || [];
          const updatedTags = [...currentTags, createdTag];
          setValue(name, updatedTags as TFieldValues[typeof name]);
        }}
      />
    );
  };

  if (!shouldRenderTagsInput) {
    return null;
  }

  return (
    <AsyncSearchableSelect<TFieldValues, ReadAllTagsQuery, TOptionShape>
      {...props}
      name={name}
      label={_label}
      placeholder={placeholder}
      disableTags={disableTags}
      multiple={true}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadAllTagsQuery,
        transformData,
      }}
      onSelectChange={onSelectChange}
      hint={hint}
      renderNoOptions={getRenderNoOptions}
      enableTagsTooltip
    />
  );
};
